<template>
  <div class="page-container">
    <Table ref="tableRef" :params="params" :request="searchData" tooltip-effect="light">
      <template #search>
        <el-form-item>
          <el-button type="primary" @click="addNew">{{$t('service.upgrade.createUpgradeTask')}}</el-button>
        </el-form-item>
      </template>
      <template #toolBar>
        <el-input-number
          v-model="duration"
          controls-position="right"
          :disabled="realTime"
          :min="1"
          size="small"
          style="margin-right: 10px"
        />
        <el-switch v-model="realTime" :active-text="$t('collect.tasklist.realTimeUpdate')" @change="onRealTimeChange" />
      </template>
      <el-table-column type="index" :label="$t('pages.indexNo')" width="60px" />
      <el-table-column prop="robotSn" :label="$t('activity.packetjoin.robotCode')" />
      <el-table-column prop="cabinetSn" :label="$t('equipment.cabinet.ctrlCode')" />
      <el-table-column prop="productType" :label="$t('service.software.productType')">
        <template #default="{row}">
          {{ productTypeMap[row.productType] }}
        </template>
      </el-table-column>
      <el-table-column prop="lastVersion" :label="$t('service.upgrade.currentVersionPlatform')" />
      <el-table-column prop="targetVersion" :label="$t('service.upgrade.targetVersionPlatform')" />
      <el-table-column prop="status" :label="$t('service.upgrade.upgradeStatus')">
        <template #default="{row}">
          {{ otaUpgradeMap[row.status] }}
        </template>
      </el-table-column>
      <el-table-column prop="errorMessage" :label="$t('service.upgrade.failureReason')">
        <template #default="{row}">
          <el-popover
            placement="top-start"
            :width="320"
            trigger="hover"
          >
            <template #default>
              {{row.errorMessage}}
            </template>
            <template #reference>
              <span class="multi-line-text">{{row.errorMessage}}</span>
            </template>
          </el-popover>
        </template>
      </el-table-column>
      <el-table-column prop="createBy" :label="$t('activity.list.creator')"/>
      <el-table-column prop="updateTime" :label="$t('collect.tasklist.operationTime')" width="150" />
    </Table>
    <el-dialog :title="$t('service.upgrade.createUpgradeTask')" top="5vh" v-model="dialogFormVisible" destroy-on-close :modal="true" :append-to-body="true">
      <div class="section-title">{{$t('collect.tasklist.selectRobot')}}</div>
      <div style="margin-bottom: 10px">
        <el-space>{{$t('service.upgrade.robotIdColon')}}<el-input v-model="robotId" :placeholder="$t('service.upgrade.enterRobotId')" clearable @keyup.enter="getUpgradeInfo" />
          <el-button size="small" type="primary" @click="getUpgradeInfo">{{$t('service.upgrade.search')}}</el-button>
        </el-space>
      </div>
      <template v-if="upgradeData.robotSN">
        <el-descriptions direction="vertical" :column="3" border>
          <el-descriptions-item :label="$t('activity.packetjoin.robotCode')">{{ upgradeData.robotSN }}</el-descriptions-item>
          <el-descriptions-item :label="$t('collect.healthscorelist.ctrlCabCode')">{{ upgradeData.cabinetSN }}</el-descriptions-item>
          <el-descriptions-item :label="$t('collect.tasklist.robotStatus')">{{ robotStatusMap[upgradeData.status] }}</el-descriptions-item>
        </el-descriptions>
        <div class="section-title">{{$t('service.upgrade.taskConfiguration')}}</div>
        <el-table class="dialog-table" :data="upgradeData.productList" border stripe>
          <el-table-column type="index" :label="$t('pages.indexNo')" width="60px" />
          <el-table-column prop="productName" :label="$t('service.software.productType')" ></el-table-column>
          <el-table-column prop="version" :label="$t('service.upgrade.currentVersionPlatformSlash')"></el-table-column>
          <el-table-column prop="upgrade" :label="$t('service.upgrade.confirmUpgrade')">
            <template #default="{ row }">
              <el-radio :label="row.productId" v-model="productId">{{ '' }}</el-radio>
            </template>
          </el-table-column>
          <el-table-column prop="targetData" :label="$t('service.upgrade.targetVersionPlatformSlash')">
            <template #default="{ row }">
              <el-select v-if="productId == row.productId" v-model="targetData" value-key="version">
                <el-option
                  v-for="item in row.productVersionList"
                  :key="item.version"
                  :label="`${item.version}`"
                  :value="item"
                />
              </el-select>
            </template>
          </el-table-column>
        </el-table>
      </template>
      <template #footer>
        <el-button :disabled="!upgradeData.robotSN" size="small" type="primary" @click="openSubmit">{{$t('activity.list.confirm')}}</el-button>
      </template>
    </el-dialog>

    <el-dialog :title="$t('service.upgrade.confirm')" top="5vh" v-model="dialogOpenFormVisible" destroy-on-close :modal="true" :append-to-body="true">
      <div class="section-title">{{$t('service.upgrade.deviceListHint')}}</div>
      <template v-if="upgradeData.robotSN">
        <el-descriptions direction="vertical" :column="3" border>
          <el-descriptions-item :label="$t('activity.packetjoin.robotCode')">{{ upgradeData.robotSN }}</el-descriptions-item>
          <el-descriptions-item :label="$t('collect.healthscorelist.ctrlCabCode')">{{ upgradeData.cabinetSN }}</el-descriptions-item>
          <el-descriptions-item :label="$t('collect.tasklist.robotStatus')">{{ robotStatusMap[upgradeData.status] }}</el-descriptions-item>
        </el-descriptions>
        <div class="section-title">{{$t('service.upgrade.content')}}</div>
        <el-table class="dialog-table" :data="currentProduct" border stripe>
          <el-table-column type="index" :label="$t('pages.indexNo')" width="60px" />
          <el-table-column prop="productName" :label="$t('service.software.productType')" ></el-table-column>
          <el-table-column prop="version" :label="$t('service.upgrade.currentVersionPlatformSlash')"></el-table-column>
          <el-table-column prop="targetData" :label="$t('service.upgrade.targetVersionPlatformSlash')">
            <template #default="{ row }">
              {{ targetData.version }}
            </template>
          </el-table-column>
        </el-table>
      </template>
      <div class="warning-box">
        <div class="warning-icon">
            <i class="el-icon-warning"></i><i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="warning-text">
            <strong>{{$t('service.upgrade.warning')}}：</strong>{{$t('service.upgrade.warningupdateInfo')}}
        </div>
      </div>
      <el-row :gutter="20" style="width: 500px;margin: 0 auto;">
        <el-col :span="14">
          <el-input :placeholder="$t('views.login.enterVerificationCode')" v-model="detailform.captcha" clearable>
            <!-- <template #prefix>
              <span class="icon iconfont icon-ota-anquan"></span>
            </template> -->
          </el-input>
        </el-col>
        <el-col :span="10" @click="validCode">
          <img class="valid-img" :src="detailform.captchaImg" alt="" srcset="">
        </el-col>
      </el-row>
      <template #footer>
        <el-button :disabled="!upgradeData.robotSN" size="small" type="primary" @click="formSubmit">{{$t('activity.list.confirm')}}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { GET, request} from "@/utils/request";
import { common } from "@/common/mixin";
import { reactive, ref, toRefs } from "vue";
// import { watch } from 'vue'
import { ElMessage } from "element-plus";
import Table from "@/components/elp/table";
import { otaUpgradeList, otaUpgradeMap, robotStatusMap, arrayToMap } from "@/common/constant"

import { reqUpgradeList, reqUpgradeVersion, reqUpgradeSend } from "@/api/upgrade"
import { useI18n } from 'vue-i18n'
export default {
  components: {
    Table,
  },
  mixins: [common],
  setup() {
    const { t } = useI18n()
    const productTypeList = ref([])
    const productTypeMap = ref({})
    const state = reactive({
      realTime: false, // 是否实时更新
      timer: null, // 定时器
      duration: 10, // 时长
      tableRef: null,
      dialogFormVisible: false,
      dialogOpenFormVisible: false,
      detailformRef: null,
      detailform: {
        robotSn: '',
        cabinetSn: '',
        productId: '',
        productName: '',
        captcha: '',
        captchaImg: '',
        captchaKey: '',
      },
      robotId: '',
      productId: '',
      targetData: '',
      upgradeData: {},
      params: [
        {
          key: "robotSn",
          value: "",
          label: t('service.upgrade.robotId'),
        },
        {
          key: "cabinetSn",
          value: "",
          label: t('service.upgrade.controlCabinetId'),
        },
        {
          type: "Select",
          key: "productType",
          value: "",
          label: t('service.software.productType'),
          options: {
            data: productTypeList,
            label: "name",
            value: "id"
          }
        },
        {
          type: "Select",
          key: "upgradeStatus",
          value: "",
          label: t('service.upgrade.upgradeStatus'),
          options: {
            data: otaUpgradeList,
            label: "label",
            value: "value"
          }
        },
        {
          type: "Date",
          key: "otaUpgradeTimeRange",
          value: [],
          label: t('service.upgrade.operationDate'),
          options: {
            "type": "datetimerange",
            "format": "YYYY-MM-DD HH:mm:ss",
            "value-format": "YYYY-MM-DD HH:mm:ss",
            "range-separator": t('activity.detail.to'),
            "start-placeholder": t('activity.list.startDate'),
            "end-placeholder": t('activity.list.endDate'),
            "default-time": [new Date(2000, 1, 1, 0, 0, 0), new Date(2000, 1, 1, 23, 59, 59)]
          }
        },
      ],
      currentProduct: []
    })
    const detailformRules = reactive({
      title: {
        required: true,
        message: t('service.forum.enterPostTitle'),
        trigger: 'blur',
      },
      content: {
        required: true,
        message: t('service.forum.enterPostContent'),
        trigger: 'blur',
      }
    })
    // watch(() => state.query,
    //   (cur, pre) => {
    //     console.log("新值:", cur, "老值:", pre);
    //     if (state.query.tag) {
    //       state.query[state.query.tag] = true
    //     }
    //   },
    //   { immediate: true, deep: true }
    // )
    const searchData = async (params) => {
      return reqUpgradeList(params)
    }
    const getProductTypeList = async () => {
      try {
        const res = await GET('getVersionProductList')
        productTypeList.value = res || []
        productTypeMap.value = arrayToMap(productTypeList.value, 'id', 'name')
      } catch (error) {
        console.error(error)
      }
    }
    const onRealTimeChange = (val) => {
      if (val && state.duration) {
        state.timer = setInterval(() => {
          state.tableRef?.update()
        }, state.duration * 1000)
      } else {
        state.timer && clearInterval(state.timer)
      }
    }
    const addNew = () => {
      state.dialogFormVisible = true
      state.robotId = ''
      state.productId = ''
      state.targetData = ''
      state.upgradeData = {}
    }
    const getUpgradeInfo = async () => {
      try {
        const res = await reqUpgradeVersion({
          id: state.robotId
        })
        state.upgradeData = res || {}
      } catch (error) {
        console.error(error)
      }
    }
    const openSubmit = async () => {
      try {
        if (!state.productId) {
          ElMessage({
            showClose: true,
            type: "warning",
            message: t('service.software.selectProductType')
          })
          return
        }
        else if (!state.targetData?.platform) {
          ElMessage({
            showClose: true,
            type: "warning",
            message: t('service.upgrade.selectTargetVersionPlatform')
          })
          return
        }
        state.currentProduct = state.upgradeData.productList.filter(item => item.productId == state.productId)
        state.dialogOpenFormVisible = true
        state.dialogFormVisible = false
        // state.tableRef?.update()
      } catch (error) {
        console.error(error)
      }
    }
    const formSubmit = async () => {
      try {
        if (!state.productId) {
          ElMessage({
            showClose: true,
            type: "warning",
            message: t('service.software.selectProductType')
          })
          return
        }
        else if (!state.targetData?.platform) {
          ElMessage({
            showClose: true,
            type: "warning",
            message: t('service.upgrade.selectTargetVersionPlatform')
          })
          return
        }
        const product = state.upgradeData.productList.find(item => item.productId == state.productId)
        await reqUpgradeSend({
          robotSn: state.upgradeData.robotSN,
          cabinetSn: state.upgradeData.cabinetSN,
          productId: state.productId,
          productName: product.productName,
          currentVersion: product.version,
          downloadUrl: state.targetData.url,
          signature: state.targetData.signature,
          platform: state.targetData.platform,
          targetVersion: state.targetData.version,
          captcha: state.detailform.captcha,
          captchaKey: state.detailform.captchaKey,
        })
        ElMessage({
          showClose: true,
          type: "success",
          message: t('activity.list.submitSuccess')
        })
        state.dialogOpenFormVisible = false
        state.dialogFormVisible = false
        state.tableRef?.update()
      } catch (error) {
        console.error(error)
      }
    }
    const validCode = () =>{
      request('validCode', {})
      .then((data) => {
        state.detailform.captchaImg = data.captcha;
        state.detailform.captchaKey = data.key;
      })
      .catch((error) => {
        console.log(error);
      });
    }
    validCode()
    getProductTypeList()
    return {
      productTypeList,
      productTypeMap,
      otaUpgradeMap,
      robotStatusMap,
      ...toRefs(state),
      detailformRules,
      searchData,
      onRealTimeChange,
      addNew,
      getUpgradeInfo,
      formSubmit,
      openSubmit,
      validCode
    }
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.list-item {
  margin-bottom: 0;
}
.box-card {
  margin-bottom: 10px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.multi-line-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.section-title {
  font-size: 16px;
  font-weight: bold;
  margin: 10px 0;
}

.warning-box {
  background-color: #fff8e1;
  border-radius: 12px;
  padding: 20px;
  margin-top: 32px;
  margin-bottom: 32px;
  border-left: 5px solid #ffb74d;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.warning-icon {
  color: #ff9800;
  font-size: 24px;
  flex-shrink: 0;
}

.warning-text {
  color: #5d4037;
  line-height: 1.6;
  font-size: 15px;
}
</style>
